import { useState, useEffect } from "react";
import { Modal } from "./SwipeableModal";
import { LogoOnly } from "../../assets/icons/logoOnly";
import { usePlatform } from "../../hooks/usePlatform";
import { useTranslation } from "react-i18next";

const WelcomeModal = ({ show, onHide }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { formatShortcut } = usePlatform();
  const { t } = useTranslation();

  const steps = [
    {
      type: "welcome",
      icon: (
        <div className="mb-2">
          <LogoOnly width="40" height="40" />
        </div>
      ),
      title: t("welcome.title"),
      subtitle: t("welcome.subtitle"),
      features: [
        {
          icon: <i className="bi bi-check-circle-fill text-success"></i>,
          title: t("welcome.features.browserIndependent.title"),
          description: t("welcome.features.browserIndependent.description"),
        },
        {
          icon: <i className="bi bi-lightbulb-fill text-warning"></i>,
          title: t("welcome.features.aiPowered.title"),
          description: t("welcome.features.aiPowered.description"),
        },
        {
          icon: <i className="bi bi-shield-lock-fill text-primary"></i>,
          title: t("welcome.features.zeroKnowledge.title"),
          description: t("welcome.features.zeroKnowledge.description"),
        },
      ],
    },
    {
      type: "feature",
      icon: (
        <div className="feature-icon-wrapper d-flex align-items-center justify-content-center position-relative mx-auto rounded-5 overflow-hidden">
          <div className="feature-icon-circles">
            <div className="circle circle-1"></div>
            <div className="circle circle-2"></div>
            <div className="circle circle-3"></div>
          </div>
          <div className="feature-main-icon position-relative z-2 mt-3">
            <svg viewBox="0 0 200 200" fill="none" width={200} height={200}>
              <circle cx="100" cy="60" r="15" fill="#10b981"></circle>
              <circle cx="70" cy="110" r="15" fill="#34d399"></circle>
              <circle cx="130" cy="110" r="15" fill="#6ee7b7"></circle>
              <path
                d="M100 60 L70 110 L130 110 Z"
                stroke="#fff"
                strokeWidth="2"
                fill="none"
                opacity="0.5"
              ></path>
            </svg>
          </div>
        </div>
      ),
      title: t("welcome.aiCategorization.title"),
      content: t("welcome.aiCategorization.content"),
      features: [
        {
          icon: <i className="bi bi-diagram-3-fill text-info"></i>,
          title: t("welcome.aiCategorization.features.multiCollection.title"),
          description: t("welcome.aiCategorization.features.multiCollection.description"),
        },
        {
          icon: <i className="bi bi-collection text-secondary"></i>,
          title: t("welcome.aiCategorization.features.intelligentCollections.title"),
          description: t("welcome.aiCategorization.features.intelligentCollections.description"),
        },
      ],
    },
    {
      type: "feature",
      icon: (
        <div className="feature-icon-wrapper d-flex align-items-center justify-content-center position-relative mx-auto rounded-5 overflow-hidden">
          <div className="shield-icon position-relative">
            <div className="shield-pulse"></div>
            <svg viewBox="0 0 200 200" fill="none" width={200} height={200}>
              <rect
                x="70"
                y="70"
                width="60"
                height="80"
                rx="10"
                fill="#10b981"
                opacity="0.8"
              ></rect>
              <circle cx="100" cy="95" r="15" fill="#1a1a1a"></circle>
              <path
                d="M85 95 Q100 80 115 95 Q100 110 85 95"
                fill="#1a1a1a"
              ></path>
              <rect
                x="90"
                y="115"
                width="20"
                height="25"
                rx="5"
                fill="#1a1a1a"
              ></rect>
            </svg>
          </div>
        </div>
      ),
      title: t("welcome.privacy.title"),
      content: t("welcome.privacy.content"),
      features: [
        {
          icon: <i className="bi bi-key-fill text-warning"></i>,
          title: t("welcome.privacy.features.aesGcm.title"),
          description: t("welcome.privacy.features.aesGcm.description"),
        },
        {
          icon: <i className="bi bi-laptop-fill text-primary"></i>,
          title: t("welcome.privacy.features.clientSide.title"),
          description: t("welcome.privacy.features.clientSide.description"),
        },
      ],
    },
    {
      type: "feature",
      icon: (
        <div className="feature-icon-wrapper d-flex align-items-center justify-content-center position-relative mx-auto rounded-5 overflow-hidden">
          <div className="workspace-grid">
            <div className="workspace-box workspace-1">•</div>
            <div className="workspace-box workspace-2">•</div>
            <div className="workspace-box workspace-3">•</div>
            <div className="workspace-box workspace-4">•</div>
          </div>
        </div>
      ),
      title: t("welcome.workspaces.title"),
      content: t("welcome.workspaces.content"),
      features: [
        {
          icon: <i className="bi bi-box-fill text-info"></i>,
          title: t("welcome.workspaces.features.contextSeparation.title"),
          description: t("welcome.workspaces.features.contextSeparation.description"),
        },
        {
          icon: <i className="bi bi-people-fill text-success"></i>,
          title: t("welcome.workspaces.features.teamCollaboration.title"),
          description: t("welcome.workspaces.features.teamCollaboration.description"),
        },
      ],
    },
    {
      type: "feature",
      icon: (
        <div className="feature-icon-wrapper d-flex align-items-center justify-content-center position-relative mx-auto rounded-5 overflow-hidden">
          <div className="keyboard-wrapper d-flex align-items-center justify-content-center gap-3 p-4">
            <div className="keyboard-key">
              <i className="bi bi-command fs-1 text-primary"></i>
            </div>
            <div className="keyboard-plus text-info fw-bold fs-4">+</div>
            <div className="keyboard-key">
              <span className="fw-bold text-primary fs-4">K</span>
            </div>
          </div>
        </div>
      ),
      title: t("welcome.shortcuts.title"),
      content: t("welcome.shortcuts.content"),
      features: [
        {
          icon: <i className="bi bi-search text-info"></i>,
          title: `${formatShortcut("K")} - ${t("welcome.shortcuts.features.quickSearch.title")}`,
          description: t("welcome.shortcuts.features.quickSearch.description"),
        },
        {
          icon: <i className="bi bi-bookmark-plus-fill text-success"></i>,
          title: `${formatShortcut("B")} - ${t("welcome.shortcuts.features.addBookmark.title")}`,
          description: t("welcome.shortcuts.features.addBookmark.description"),
        },
        {
          icon: <i className="bi bi-collection-fill text-warning"></i>,
          title: `${formatShortcut("U")} - ${t("welcome.shortcuts.features.newCollection.title")}`,
          description: t("welcome.shortcuts.features.newCollection.description"),
        },
      ],
    },
    {
      type: "cta",
      icon: <div className="fs-1">🚀</div>,
      title: t("welcome.cta.title"),
      subtitle: t("welcome.cta.subtitle"),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const goToStep = (index) => {
    if (index !== currentStep && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(index);
        setIsAnimating(false);
      }, 300);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!show) return;

      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "Escape") onHide();
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentStep]);

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      dialogClassName="welcome-modal"
      contentClassName="border-0"
    >
      <Modal.Body className="p-0 position-relative">
        {/* Modal Content */}
        <div className="p-5 pb-4">
          <div className={`slide ${isAnimating ? "animating" : ""}`}>
            {step.type === "welcome" && (
              <div className="text-center">
                {step.icon}
                <h1 className="h1 fw-bold mb-3">{step.title}</h1>
                <p className="fs-5 text-muted mb-4">{step.subtitle}</p>

                <div className="mx-auto" style={{ maxWidth: "600px" }}>
                  {step.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="welcome-feature-item bg-light d-flex align-items-start gap-3 p-3 rounded-4 mb-3"
                    >
                      <div className="icon bg-primary bg-opacity-10">
                        {feature.icon}
                      </div>
                      <div className="text-start">
                        <h6 className="mb-1">{feature.title}</h6>
                        <p className="text-muted small mb-0">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step.type === "feature" && (
              <div className="row align-items-center g-4">
                <div className="col-lg-5 text-center">{step.icon}</div>
                <div className="col-lg-7">
                  <h2 className="mb-2 h2">{step.title}</h2>
                  <p className="text-secondary mb-4 fs-6">{step.content}</p>

                  {step.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="welcome-feature-item bg-light d-flex align-items-start gap-3 p-3 rounded-4 mb-3"
                    >
                      <div className="icon bg-primary bg-opacity-10">
                        {feature.icon}
                      </div>
                      <div>
                        <h6 className="mb-1">{feature.title}</h6>
                        <p className="text-secondary small mb-0">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step.type === "cta" && (
              <div className="text-center py-5">
                {step.icon}
                <h2 className="mb-3 fs-1">{step.title}</h2>
                <p className="fs-5 text-secondary mb-5">{step.subtitle}</p>

                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <button
                    className="btn btn-primary btn-welcome-primary"
                    onClick={onHide}
                  >
                    {t("welcome.cta.getStarted")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation dots */}
          <div className="nav-dots">
            {steps.map((_, index) => (
              <button
                key={index}
                className={`nav-dot ${
                  index === currentStep ? "bg-info" : "bg-secondary"
                }`}
                onClick={() => goToStep(index)}
              />
            ))}
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="d-flex justify-content-between align-items-center px-5 pb-4">
          <button
            className="btn btn-none text-secondary text-decoration-none btn-welcome-skip"
            onClick={onHide}
          >
            {t("welcome.navigation.skipTour")}
          </button>

          <div className="d-flex gap-2">
            {!isLastStep && (
              <button
                className="btn btn-primary btn-welcome-nav"
                onClick={handleNext}
                disabled={isAnimating}
              >
                {t("welcome.navigation.next")}
              </button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default WelcomeModal;
